import DataGrid, {
  Column,
  Button as GridButton,
  Item,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import {
  createClientAccountsUser,
  deleteClientAccountsUser,
  assignClientAccountsUserP2,
  removeClientAccountsUserP2,
  getUserGroups,
  inviteClientAccountsGuestUser,
  listClientAccountsDomains,
  listClientAccountsUsers,
  listClientPortalTenants,
  offboardClientAccountsUser,
  setUserGroups,
  updateClientAccountsUser,
  type CreatedClientUser,
  type UserGroupMembership,
  type VerifiedDomain
} from '../../../api/clientAccountsUsersAdmin';
import { fetchClientLicenseSummary } from '../../../api/clientLicenseSummary';
import { listClientAccountsGroups } from '../../../api/clientAccountsAdmin';
import {
  showConfirmDialog,
  OPS_SITE_GRID_PROPS,
  OPS_TOOLBAR_BUTTON,
  OpsGridToolbarItems,
  OpsGridToolbarSeparator,
  OpsSiteGridChrome,
  exportOpsGridCsv,
  exportOpsGridExcel,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  resetOpsGridLayout,
  showDeleteConfirmDialog,
  showOffboardConfirmDialog,
  showErrorToast,
  showSuccessToast,
  showWarningToast,
  useShellLayout
} from '@jarvis/platform-ui';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router';
import {
  GRID_COLUMNS,
  GRID_SCHEMA_VERSION,
  GRID_STATE_KEY,
  buildUpn,
  sanitizeAccountName,
  sortRows,
  splitUpn,
  toOptimisticUserRow,
  type ClientUserGridRow
} from './HvgClients/hvgClientsColumns';
import {
  HvgClientUserFormPopup,
  emptyUserForm,
  type CreateProgressState,
  type UserFormState
} from './HvgClients/HvgClientUserFormPopup';
import { composeDisplayName } from './HvgClients/hvgClientFormLayout';
import {
  HvgClientGuestInvitePopup,
  emptyGuestInviteForm,
  type GuestInviteFormState,
  type GuestInviteProgressState
} from './HvgClients/HvgClientGuestInvitePopup';

function AccountEnabledPill({ enabled }: { enabled: boolean }) {
  const style: CSSProperties = {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    background: enabled ? 'var(--ops-status-succeeded-bg, #dcfce7)' : 'var(--ops-status-failed-bg, #fee2e2)',
    color: enabled ? 'var(--ops-status-succeeded-fg, #166534)' : 'var(--ops-status-failed-fg, #b91c1c)'
  };
  return <span style={style}>{enabled ? 'Enabled' : 'Disabled'}</span>;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isMembershipReplicationError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('replicat') ||
    m.includes('does not exist') ||
    m.includes('not found') ||
    m.includes('not yet') ||
    m.includes('not a valid reference') ||
    m.includes('try again')
  );
}

/** Newly created/invited users often need a few seconds before Entra accepts group membership. */
async function assignGroupsWithRetry(
  tenantId: string,
  userId: string,
  groupIds: string[]
): Promise<void> {
  const retryDelaysMs = [3_000, 4_000, 5_000, 6_000, 8_000, 10_000];
  await delay(2_000);

  let lastError: string | null = null;
  for (let attempt = 0; attempt < retryDelaysMs.length + 1; attempt++) {
    try {
      await setUserGroups(tenantId, userId, groupIds);
      return;
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
      if (!isMembershipReplicationError(lastError) || attempt >= retryDelaysMs.length) break;
      await delay(retryDelaysMs[attempt]!);
    }
  }

  throw new Error(lastError ?? 'Group membership assignment failed');
}

/** One toast for create so a success banner never hides a P2 failure. */
function notifyUserLifecycleOutcome(args: {
  okMessage: string;
  licenseWarning?: string | null;
  licenseFailMessage: string;
}): void {
  if (args.licenseWarning) {
    showWarningToast(args.licenseFailMessage);
    return;
  }
  showSuccessToast(args.okMessage);
}

export function HvgClientsAdminPage() {
  const location = useLocation();
  const pendingFocusUserIdRef = useRef<string | null>(
    (location.state as { focusUserId?: string } | null)?.focusUserId ?? null
  );
  const gridRef = useRef<DataGridRef>(null);
  const loadGenRef = useRef(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpsertsRef = useRef<Map<string, ClientUserGridRow>>(new Map());
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [rows, setRows] = useState<ClientUserGridRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadPending, setInitialLoadPending] = useState(true);
  const [focusedRowKey, setFocusedRowKey] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<UserFormState>(emptyUserForm);
  const [formTab, setFormTab] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasP2License, setHasP2License] = useState(false);
  const [p2AvailableUnits, setP2AvailableUnits] = useState<number | null>(null);
  const [p2LicenseBusy, setP2LicenseBusy] = useState(false);
  const [createProgress, setCreateProgress] = useState<CreateProgressState | null>(null);
  const [createdUser, setCreatedUser] = useState<CreatedClientUser | null>(null);
  const [groupOptions, setGroupOptions] = useState<UserGroupMembership[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [domains, setDomains] = useState<VerifiedDomain[]>([]);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [guestInviteOpen, setGuestInviteOpen] = useState(false);
  const [guestFormTab, setGuestFormTab] = useState(0);
  const [guestForm, setGuestForm] = useState<GuestInviteFormState>(emptyGuestInviteForm);
  const [guestFormError, setGuestFormError] = useState<string | null>(null);
  const [guestSaving, setGuestSaving] = useState(false);
  const [guestProgress, setGuestProgress] = useState<GuestInviteProgressState | null>(null);
  const [guestGroupOptions, setGuestGroupOptions] = useState<UserGroupMembership[]>([]);
  const [loadingGuestGroups, setLoadingGuestGroups] = useState(false);

  const focusedRow = focusedRowKey ? rows.find((r) => r.id === focusedRowKey) ?? null : null;
  const { isNarrow } = useShellLayout();
  const gridStateKey = `${GRID_STATE_KEY}:${tenantId ?? 'none'}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listClientPortalTenants();
        if (cancelled) return;
        if (items.length === 0) {
          setTenantError('No client tenants are configured. Add ClientGraph__Tenants__N__* to WebOps env.');
          setTenantId(null);
        } else {
          setTenantId(items[0].tenantId);
          setTenantError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) setTenantError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tenantId) setInitialLoadPending(true);
  }, [tenantId]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setInitialLoadPending(false);
      return;
    }

    const gen = ++loadGenRef.current;
    setLoading(true);
    try {
      const data = await listClientAccountsUsers(tenantId, { skip: 0, take: 1000 });
      if (gen !== loadGenRef.current) return;

      const items = Array.isArray(data?.items) ? data.items : [];
      const serverIds = new Set(items.map((item) => item.id));
      for (const id of pendingDeleteIdsRef.current) {
        if (!serverIds.has(id)) pendingDeleteIdsRef.current.delete(id);
      }

      setRows(
        sortRows(
          items
            .filter((item) => !pendingDeleteIdsRef.current.has(item.id))
            .map((item) => {
              const pending = pendingUpsertsRef.current.get(item.id);
              if (!pending) return item;
              const merged = { ...item, ...pending, accountEnabled: pending.accountEnabled };
              if (
                item.displayName === pending.displayName &&
                item.userPrincipalName === pending.userPrincipalName &&
                item.accountEnabled === pending.accountEnabled
              ) {
                pendingUpsertsRef.current.delete(item.id);
              }
              return merged;
            })
        )
      );
      const focusId = pendingFocusUserIdRef.current;
      if (focusId && items.some((item) => item.id === focusId)) {
        setFocusedRowKey(focusId);
        pendingFocusUserIdRef.current = null;
      }
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setRows([]);
      showErrorToast(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
        setInitialLoadPending(false);
      }
    }
  }, [tenantId]);

  const scheduleRefresh = useCallback(
    (delayMs = 750) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void load();
      }, delayMs);
    },
    [load]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = async () => {
    setEditingId(null);
    setFormMode('create');
    setHasP2License(false);
    setP2AvailableUnits(null);
    setForm(emptyUserForm());
    setFormTab(0);
    setFormError(null);
    setCreateProgress(null);
    setCreatedUser(null);
    setGroupOptions([]);
    setDomains([]);
    setDomainsError(null);
    setEditorOpen(true);

    if (!tenantId) return;

    // Load independently — a domains Graph failure must not wipe group membership options.
    setLoadingDomains(true);
    setLoadingGroups(true);

    const domainsTask = (async () => {
      try {
        const domainList = await listClientAccountsDomains(tenantId);
        setDomains(domainList);
        setDomainsError(null);
        const defaultDomain = domainList.find((d) => d.isDefault)?.id ?? domainList[0]?.id ?? '';
        if (defaultDomain) setForm((f) => ({ ...f, domain: f.domain || defaultDomain }));
      } catch (e) {
        setDomains([]);
        const message = e instanceof Error ? e.message : String(e);
        setDomainsError(message);
        // Fallback: domains already present on users in the grid.
        const fromRows = [
          ...new Set(
            rows
              .map((r) => splitUpn(r.userPrincipalName).domain)
              .filter((d) => d.length > 0)
              .map((d) => d.toLowerCase())
          )
        ];
        if (fromRows.length > 0) {
          setForm((f) => ({ ...f, domain: f.domain || fromRows[0] }));
        }
      } finally {
        setLoadingDomains(false);
      }
    })();

    const groupsTask = (async () => {
      try {
        const groupsData = await listClientAccountsGroups(tenantId);
        const security = groupsData.items.filter((g) => g.securityEnabled);
        setGroupOptions(
          security.map((g) => ({
            groupId: g.id,
            displayName: g.displayName,
            isMember: false,
            assignedSprRoles: g.assignedSprRoles
          }))
        );
      } catch (e) {
        setGroupOptions([]);
        showErrorToast(e instanceof Error ? e.message : 'Failed to load groups');
      } finally {
        setLoadingGroups(false);
      }
    })();

    await Promise.all([domainsTask, groupsTask]);
  };

  const closeEditor = () => {
    if (createProgress && !createProgress.done && !createProgress.error) return;
    setEditorOpen(false);
    setCreateProgress(null);
    setCreatedUser(null);
    setFormError(null);
    setFormTab(0);
    setFormMode('create');
    setP2LicenseBusy(false);
  };

  const refreshLicenseSummary = async () => {
    if (!tenantId) {
      setP2AvailableUnits(null);
      return;
    }
    try {
      const summary = await fetchClientLicenseSummary(tenantId);
      setP2AvailableUnits(summary.availableUnits);
    } catch {
      setP2AvailableUnits(null);
    }
  };

  const openUserForm = async (row: ClientUserGridRow, mode: 'edit' | 'view') => {
    const { accountName, domain } = splitUpn(row.userPrincipalName);
    setEditingId(row.id);
    setFormMode(mode);
    setFocusedRowKey(row.id);
    setHasP2License(Boolean(row.hasP2License));
    setForm({
      givenName: row.givenName ?? '',
      surname: row.surname ?? '',
      accountName,
      domain,
      mail: row.mail ?? '',
      department: row.department ?? '',
      jobTitle: row.jobTitle ?? '',
      company: row.company ?? '',
      employeeType: row.employeeType ?? '',
      employeeHireDate: row.employeeHireDate ?? null,
      officeLocation: row.officeLocation ?? '',
      businessPhone: row.businessPhone ?? '',
      groupIds: []
    });
    setFormTab(0);
    setFormError(null);
    setCreateProgress(null);
    setCreatedUser(null);
    setEditorOpen(true);
    void refreshLicenseSummary();

    if (!tenantId) return;
    setLoadingGroups(true);
    try {
      const memberships = await getUserGroups(tenantId, row.id);
      setGroupOptions(memberships);
      setForm((f) => ({
        ...f,
        groupIds: memberships.filter((m) => m.isMember).map((m) => m.groupId)
      }));
    } catch (e) {
      setGroupOptions([]);
      showErrorToast(e instanceof Error ? e.message : 'Failed to load group memberships');
    } finally {
      setLoadingGroups(false);
    }
  };

  const openEdit = async (row: ClientUserGridRow) => {
    await openUserForm(row, 'edit');
  };

  const openView = async (row: ClientUserGridRow) => {
    await openUserForm(row, 'view');
  };

  const switchEditorToEdit = () => {
    setFormMode('edit');
  };

  const applyP2StateToRow = (userId: string, nextHasP2: boolean) => {
    setHasP2License(nextHasP2);
    setRows((prev) =>
      prev.map((r) => (r.id === userId ? { ...r, hasP2License: nextHasP2 } : r))
    );
    const pending = pendingUpsertsRef.current.get(userId);
    if (pending) {
      pendingUpsertsRef.current.set(userId, { ...pending, hasP2License: nextHasP2 });
    }
  };

  const assignP2FromEditor = async () => {
    if (!tenantId || !editingId) return;
    setP2LicenseBusy(true);
    try {
      const result = await assignClientAccountsUserP2(tenantId, editingId);
      if (result.success) {
        applyP2StateToRow(editingId, true);
        showSuccessToast('Entra ID P2 assigned.');
        if (result.warning) showErrorToast(result.warning);
        await refreshLicenseSummary();
      } else {
        showErrorToast(result.warning ?? 'Failed to assign P2 license.');
      }
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to assign P2 license');
    } finally {
      setP2LicenseBusy(false);
    }
  };

  const removeP2FromEditor = async () => {
    if (!tenantId || !editingId) return;
    if (
      !(await showConfirmDialog({
        title: 'Remove P2 license',
        message: 'Remove the Entra ID P2 license from this user?',
        confirmText: 'Remove',
        danger: true
      }))
    ) {
      return;
    }
    setP2LicenseBusy(true);
    try {
      const result = await removeClientAccountsUserP2(tenantId, editingId);
      if (result.success) {
        applyP2StateToRow(editingId, false);
        showSuccessToast('Entra ID P2 removed.');
        if (result.warning) showErrorToast(result.warning);
        await refreshLicenseSummary();
      } else {
        showErrorToast(result.warning ?? 'Failed to remove P2 license.');
      }
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to remove P2 license');
    } finally {
      setP2LicenseBusy(false);
    }
  };

  const openGuestInvite = async () => {
    setGuestForm(emptyGuestInviteForm());
    setGuestFormTab(0);
    setGuestFormError(null);
    setGuestProgress(null);
    setGuestGroupOptions([]);
    setGuestInviteOpen(true);
    if (!tenantId) return;

    setLoadingGuestGroups(true);
    try {
      const groupsData = await listClientAccountsGroups(tenantId);
      const security = groupsData.items.filter((g) => g.securityEnabled);
      setGuestGroupOptions(
        security.map((g) => ({
          groupId: g.id,
          displayName: g.displayName,
          isMember: false,
          assignedSprRoles: g.assignedSprRoles
        }))
      );
    } catch (e) {
      setGuestGroupOptions([]);
      showErrorToast(e instanceof Error ? e.message : 'Failed to load groups');
    } finally {
      setLoadingGuestGroups(false);
    }
  };

  const closeGuestInvite = () => {
    if (guestProgress && !guestProgress.done && !guestProgress.error) return;
    setGuestInviteOpen(false);
    setGuestProgress(null);
    setGuestFormError(null);
    setGuestFormTab(0);
  };

  const inviteGuest = async () => {
    if (!tenantId) return;
    if (!guestForm.givenName.trim()) {
      setGuestFormTab(0);
      setGuestFormError('First name is required.');
      return;
    }
    if (!guestForm.surname.trim()) {
      setGuestFormTab(0);
      setGuestFormError('Last name is required.');
      return;
    }
    if (!guestForm.email.trim()) {
      setGuestFormTab(0);
      setGuestFormError('Email is required.');
      return;
    }

    const displayName = composeDisplayName(guestForm.givenName, guestForm.surname);

    setGuestSaving(true);
    setGuestFormError(null);
    try {
      setGuestProgress({
        title: 'Inviting…',
        detail: 'Sending a Microsoft Entra guest invitation email.',
        error: null,
        done: false
      });

      const desiredGroups = [...guestForm.groupIds];
      const invited = await inviteClientAccountsGuestUser(tenantId, {
        givenName: guestForm.givenName.trim(),
        surname: guestForm.surname.trim(),
        email: guestForm.email.trim(),
        department: guestForm.department.trim() || null,
        jobTitle: guestForm.jobTitle.trim() || null,
        company: guestForm.company.trim() || null,
        groupIds: desiredGroups.length > 0 ? desiredGroups : undefined
      });

      const row = toOptimisticUserRow(
        {
          ...invited,
          displayName,
          givenName: guestForm.givenName.trim(),
          surname: guestForm.surname.trim()
        },
        tenantId
      );
      pendingUpsertsRef.current.set(row.id, row);
      setRows((prev) => sortRows([...prev.filter((r) => r.id !== row.id), row]));
      setFocusedRowKey(row.id);

      // Close quickly — membership often waits on Entra replication (same as group SPR assign).
      setGuestProgress({
        title: 'Done',
        detail:
          desiredGroups.length > 0
            ? 'Invitation sent. Group memberships are being assigned in the background…'
            : 'Microsoft emailed the invitation. The guest must redeem it before signing in.',
        error: null,
        done: true
      });
      showSuccessToast(
        desiredGroups.length > 0
          ? 'Guest invitation sent. Assigning groups in the background…'
          : 'Guest invitation sent.'
      );
      scheduleRefresh(2500);

      if (desiredGroups.length > 0) {
        void (async () => {
          try {
            // Always retry client-side: server may have returned a membershipWarning due to lag.
            await assignGroupsWithRetry(tenantId, invited.id, desiredGroups);
            showSuccessToast('Guest group memberships assigned.');
            scheduleRefresh(2500);
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Group membership assignment failed';
            showErrorToast(
              `Invitation sent, but groups were not assigned yet (${message}). Open the user and save Membership again.`
            );
            scheduleRefresh(2500);
          }
        })();
      }
    } catch (e) {
      setGuestProgress(null);
      setGuestFormError(e instanceof Error ? e.message : 'Invite failed');
    } finally {
      setGuestSaving(false);
    }
  };

  const save = async () => {
    if (!tenantId) return;
    if (!form.givenName.trim()) {
      setFormTab(0);
      setFormError('First name is required.');
      return;
    }
    if (!form.surname.trim()) {
      setFormTab(0);
      setFormError('Last name is required.');
      return;
    }

    const displayName = composeDisplayName(form.givenName, form.surname);

    setSaving(true);
    setFormError(null);
    try {
      if (editingId == null) {
        const accountName = sanitizeAccountName(form.accountName);
        if (!accountName) {
          setFormTab(0);
          setFormError('Account name is required.');
          setSaving(false);
          return;
        }
        if (!form.domain.trim()) {
          setFormTab(0);
          setFormError('Domain is required.');
          setSaving(false);
          return;
        }
        const userPrincipalName = buildUpn(accountName, form.domain);
        if (!userPrincipalName) {
          setFormTab(0);
          setFormError('Could not build a valid UPN from account name and domain.');
          setSaving(false);
          return;
        }

        setCreateProgress({
          title: 'Creating user…',
          detail: 'Saving the member user in Entra.',
          error: null,
          done: false
        });

        const created = await createClientAccountsUser(tenantId, {
          givenName: form.givenName.trim(),
          surname: form.surname.trim(),
          userPrincipalName,
          mailNickname: accountName,
          mail: form.mail.trim() || null,
          department: form.department.trim() || null,
          jobTitle: form.jobTitle.trim() || null,
          company: form.company.trim() || null,
          employeeType: form.employeeType.trim() || null,
          employeeHireDate: form.employeeHireDate,
          officeLocation: form.officeLocation.trim() || null,
          businessPhone: form.businessPhone.trim() || null
        });
        setCreatedUser(created);

        const row = toOptimisticUserRow(
          {
            ...created,
            displayName,
            employeeHireDate:
              created.employeeHireDate ??
              form.employeeHireDate
          },
          tenantId
        );
        pendingUpsertsRef.current.set(row.id, row);
        setRows((prev) => sortRows([...prev.filter((r) => r.id !== row.id), row]));
        setFocusedRowKey(row.id);

        const desiredGroups = [...form.groupIds];
        const licenseWarning = created.licenseWarning?.trim() || null;
        setCreateProgress({
          title: 'Done',
          detail: licenseWarning
            ? `User is ready, but P2 was not assigned: ${licenseWarning}${
                desiredGroups.length > 0
                  ? ' Group memberships are being assigned in the background. Copy the temporary password before closing.'
                  : ' Open the user and use Assign P2 when ready. Copy the temporary password before closing.'
              }`
            : desiredGroups.length > 0
              ? 'User is ready. Group memberships are being assigned in the background. Copy the temporary password before closing.'
              : 'User is ready. Copy the temporary password before closing.',
          error: null,
          done: true
        });
        notifyUserLifecycleOutcome({
          okMessage:
            desiredGroups.length > 0
              ? 'User created. Assigning groups in the background…'
              : 'User created.',
          licenseWarning,
          licenseFailMessage: `User created, but P2 license was not assigned: ${licenseWarning}`
        });
        scheduleRefresh(2500);

        if (desiredGroups.length > 0) {
          void (async () => {
            try {
              await assignGroupsWithRetry(tenantId, created.id, desiredGroups);
              showSuccessToast('Group memberships assigned.');
              scheduleRefresh(2500);
            } catch (e) {
              const message = e instanceof Error ? e.message : 'Group membership assignment failed';
              showErrorToast(
                `User created, but groups were not assigned yet (${message}). Open the user and save Membership again.`
              );
              scheduleRefresh(2500);
            }
          })();
        }
      } else {
        await updateClientAccountsUser(tenantId, editingId, {
          givenName: form.givenName.trim(),
          surname: form.surname.trim(),
          mail: form.mail.trim(),
          department: form.department.trim(),
          jobTitle: form.jobTitle.trim(),
          company: form.company.trim(),
          employeeType: form.employeeType.trim(),
          employeeHireDate: form.employeeHireDate,
          updateEmployeeHireDate: true,
          officeLocation: form.officeLocation.trim(),
          businessPhone: form.businessPhone.trim()
        });
        await setUserGroups(tenantId, editingId, form.groupIds);

        const existing = rows.find((r) => r.id === editingId);
        const upn = existing?.userPrincipalName ?? buildUpn(form.accountName, form.domain);
        const row: ClientUserGridRow = {
          ...(existing ??
            toOptimisticUserRow(
              {
                id: editingId,
                displayName,
                userPrincipalName: upn,
                mail: form.mail.trim() || null,
                accountEnabled: true,
                department: form.department.trim() || null,
                jobTitle: form.jobTitle.trim() || null,
                company: form.company.trim() || null,
                givenName: form.givenName.trim(),
                surname: form.surname.trim(),
                employeeType: form.employeeType.trim() || null,
                employeeHireDate: form.employeeHireDate,
                officeLocation: form.officeLocation.trim() || null,
                businessPhone: form.businessPhone.trim() || null
              },
              tenantId
            )),
          displayName,
          givenName: form.givenName.trim(),
          surname: form.surname.trim(),
          mail: form.mail.trim() || null,
          department: form.department.trim() || null,
          jobTitle: form.jobTitle.trim() || null,
          company: form.company.trim() || null,
          employeeType: form.employeeType.trim() || null,
          employeeHireDate: form.employeeHireDate,
          officeLocation: form.officeLocation.trim() || null,
          businessPhone: form.businessPhone.trim() || null
        };
        pendingUpsertsRef.current.set(row.id, row);
        setRows((prev) => sortRows(prev.map((r) => (r.id === row.id ? row : r))));
        showSuccessToast('User updated.');
        setEditorOpen(false);
        scheduleRefresh(2500);
      }
    } catch (e) {
      setCreateProgress(null);
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row: ClientUserGridRow) => {
    if (!tenantId) return;
    if (!(await showDeleteConfirmDialog(`Delete user "${row.displayName}"? This cannot be undone.`))) return;
    try {
      await deleteClientAccountsUser(tenantId, row.id);
      pendingDeleteIdsRef.current.add(row.id);
      pendingUpsertsRef.current.delete(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      if (focusedRowKey === row.id) setFocusedRowKey(null);
      showSuccessToast('User deleted.');
      if (editingId === row.id) setEditorOpen(false);
      scheduleRefresh();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete user');
    }
  };

  const removeFromEditor = async () => {
    if (!tenantId || editingId == null) return;
    const row = rows.find((r) => r.id === editingId);
    if (!row) return;
    setSaving(true);
    try {
      await removeRow(row);
    } finally {
      setSaving(false);
    }
  };

  const setFocusedAccountEnabled = async (enabled: boolean) => {
    if (!tenantId || !focusedRow) {
      showErrorToast('Select a user first.');
      return;
    }
    if (focusedRow.accountEnabled === enabled) {
      showSuccessToast(enabled ? 'User is already enabled.' : 'User is already disabled.');
      return;
    }
    try {
      const updated = await updateClientAccountsUser(tenantId, focusedRow.id, { accountEnabled: enabled });
      const row: ClientUserGridRow = { ...focusedRow, accountEnabled: updated.accountEnabled };
      pendingUpsertsRef.current.set(row.id, row);
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      showSuccessToast(enabled ? 'User enabled.' : 'User disabled.');
      scheduleRefresh(1500);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : `Failed to ${enabled ? 'enable' : 'disable'} user`);
    }
  };

  const offboardFocusedUser = async () => {
    if (!tenantId || !focusedRow) {
      showErrorToast('Select a user first.');
      return;
    }
    if (!(await showOffboardConfirmDialog(focusedRow.displayName, focusedRow.userPrincipalName))) return;

    try {
      const result = await offboardClientAccountsUser(tenantId, focusedRow.id);
      const row: ClientUserGridRow = { ...focusedRow, accountEnabled: false };
      pendingUpsertsRef.current.set(row.id, row);
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      showSuccessToast('User off-boarded.');
      if (result.licenseWarning) showErrorToast(result.licenseWarning);
      if (result.emailWarning) showErrorToast(result.emailWarning);
      if (result.groupsRemainingWarning.length > 0) {
        showErrorToast(`Some groups may remain: ${result.groupsRemainingWarning.join(', ')}`);
      }
      scheduleRefresh(1500);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to off-board user');
    }
  };

  const onFormChange = (patch: Partial<UserFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const domainOptions = (() => {
    const fromGraph = domains.map((d) => d.id);
    const fromRows = rows
      .map((r) => splitUpn(r.userPrincipalName).domain)
      .filter((d) => d.length > 0);
    return [...new Set([...fromGraph, ...fromRows].map((d) => d.toLowerCase()))].sort((a, b) =>
      a.localeCompare(b)
    );
  })();

  if (bootstrapping) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <LoadIndicator visible height={20} width={20} />
        <span>Loading…</span>
      </div>
    );
  }

  if (tenantError || !tenantId) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem' }}>
        <div style={{ color: 'var(--dx-color-danger, #dc2626)', fontSize: 14 }}>
          {tenantError ?? 'No client tenant configured.'}
        </div>
      </div>
    );
  }

  if (initialLoadPending) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <LoadIndicator visible height={20} width={20} />
        <span>Loading users…</span>
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled={false}
          allowColumnReordering
          allowColumnResizing
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          focusedRowEnabled
          focusedRowKey={focusedRowKey ?? undefined}
          onFocusedRowChanged={(e) => {
            const key = e.row?.key;
            setFocusedRowKey(typeof key === 'string' ? key : null);
          }}
          noDataText="No users found in this tenant."
          onRowDblClick={(e) => onGridRowDblClick(e, (row) => void openView(row))}
          onKeyDown={(e) => onGridFocusedRowEnter(e, (row) => void openView(row))}
        >
          <OpsSiteGridChrome
            stateStorageKey={`${GRID_STATE_KEY}:${tenantId}`}
            schemaVersion={GRID_SCHEMA_VERSION}
            stateColumns={GRID_COLUMNS}
            searchPlaceholder="Search users…"
            pageSize={50}
          />
          <Toolbar>
            {isNarrow ? (
              <OpsGridToolbarItems
                gridRef={gridRef}
                loading={loading}
                onRefresh={() => void load()}
                refreshHint="Refresh users"
                newItem={{ hint: 'Add user', onClick: () => void openCreate() }}
                viewItem={{
                  hint: 'View user',
                  active: focusedRow != null,
                  onClick: () => {
                    if (focusedRow) void openView(focusedRow);
                  }
                }}
                editItem={{
                  hint: 'Edit user',
                  active: focusedRow != null,
                  onClick: () => {
                    if (focusedRow) void openEdit(focusedRow);
                  }
                }}
                deleteItem={{
                  hint: 'Delete user',
                  active: focusedRow != null,
                  onClick: () => {
                    if (focusedRow) void removeRow(focusedRow);
                  }
                }}
                export={{ fileName: 'hvg-clients-export', worksheetName: 'HVG Clients', hint: 'Export users' }}
                resetLayout={{ storageKey: gridStateKey }}
                extraMenuItems={[
                  {
                    id: 'new-ce-account',
                    text: 'New CE Account',
                    icon: 'email',
                    onClick: () => void openGuestInvite()
                  },
                  {
                    id: 'enable-user',
                    text: 'Enable account',
                    icon: 'check',
                    disabled: !focusedRow || focusedRow.accountEnabled,
                    onClick: () => void setFocusedAccountEnabled(true)
                  },
                  {
                    id: 'disable-user',
                    text: 'Disable account',
                    icon: 'close',
                    disabled: !focusedRow || !focusedRow.accountEnabled,
                    onClick: () => void setFocusedAccountEnabled(false)
                  },
                  {
                    id: 'offboard-user',
                    text: 'Off-board',
                    icon: 'export',
                    disabled: !focusedRow,
                    onClick: () => void offboardFocusedUser()
                  }
                ]}
              />
            ) : (
              <>
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'eyeopen',
                    hint: 'View user',
                    elementAttr: { title: 'View user' },
                    type: focusedRow ? 'default' : 'normal',
                    stylingMode: focusedRow ? 'contained' : 'outlined',
                    disabled: !focusedRow || loading,
                    onClick: () => {
                      if (focusedRow) void openView(focusedRow);
                    }
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'edit',
                    hint: 'Edit user',
                    elementAttr: { title: 'Edit user' },
                    type: focusedRow ? 'default' : 'normal',
                    stylingMode: focusedRow ? 'contained' : 'outlined',
                    disabled: !focusedRow || loading,
                    onClick: () => {
                      if (focusedRow) void openEdit(focusedRow);
                    }
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'trash',
                    hint: 'Delete user',
                    elementAttr: { title: 'Delete user' },
                    type: focusedRow ? 'danger' : 'normal',
                    stylingMode: focusedRow ? 'contained' : 'outlined',
                    disabled: !focusedRow || loading,
                    onClick: () => {
                      if (focusedRow) void removeRow(focusedRow);
                    }
                  }}
                />
                <OpsGridToolbarSeparator />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    icon: 'plus',
                    hint: 'Add user',
                    elementAttr: { title: 'Add user' },
                    type: 'default',
                    stylingMode: 'contained',
                    disabled: loading,
                    onClick: () => void openCreate()
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    icon: 'email',
                    hint: 'New CE Account',
                    elementAttr: { title: 'New CE Account' },
                    type: 'default',
                    stylingMode: 'contained',
                    disabled: loading,
                    onClick: () => void openGuestInvite()
                  }}
                />
                <OpsGridToolbarSeparator />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'check',
                    hint: 'Enable account',
                    elementAttr: { title: 'Enable account' },
                    disabled: !focusedRow || focusedRow.accountEnabled || loading,
                    onClick: () => void setFocusedAccountEnabled(true)
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'close',
                    hint: 'Disable account',
                    elementAttr: { title: 'Disable account' },
                    disabled: !focusedRow || !focusedRow.accountEnabled || loading,
                    onClick: () => void setFocusedAccountEnabled(false)
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'export',
                    hint: 'Off-board user',
                    elementAttr: { title: 'Off-board user' },
                    disabled: !focusedRow || loading,
                    onClick: () => void offboardFocusedUser()
                  }}
                />
                <OpsGridToolbarSeparator />
                <Item
                  location="after"
                  widget="dxDropDownButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'export',
                    hint: 'Export users',
                    elementAttr: { title: 'Export users' },
                    displayExpr: 'text',
                    keyExpr: 'id',
                    dropDownOptions: { width: 160 },
                    items: [
                      { id: 'excel', text: 'Excel', icon: 'exportxlsx' },
                      { id: 'csv', text: 'CSV', icon: 'export' }
                    ],
                    onItemClick: (e: { itemData?: { id?: string } }) => {
                      const instance = gridRef.current?.instance();
                      if (e.itemData?.id === 'excel') {
                        exportOpsGridExcel(instance, 'hvg-clients-export', 'HVG Clients');
                      } else if (e.itemData?.id === 'csv') {
                        exportOpsGridCsv(instance, 'hvg-clients-export');
                      }
                    }
                  }}
                />
                <OpsGridToolbarSeparator />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'refresh',
                    hint: 'Refresh users',
                    elementAttr: { title: 'Refresh users' },
                    disabled: loading,
                    onClick: () => void load()
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'revert',
                    hint: 'Reset column layout',
                    elementAttr: { title: 'Reset column layout' },
                    onClick: () => resetOpsGridLayout(gridStateKey, gridRef)
                  }}
                />
                <Item
                  location="after"
                  widget="dxButton"
                  options={{
                    ...OPS_TOOLBAR_BUTTON,
                    icon: 'columnchooser',
                    hint: 'Choose columns',
                    elementAttr: { title: 'Choose columns' },
                    onClick: () => gridRef.current?.instance()?.showColumnChooser()
                  }}
                />
              </>
            )}
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <Column
            type="buttons"
            name="actions"
            caption="Actions"
            width={110}
            fixed
            fixedPosition="left"
            visible={false}
            showInColumnChooser
          >
            <GridButton
              hint="View user"
              icon="eyeopen"
              onClick={(e) => {
                const row = e.row?.data as ClientUserGridRow | undefined;
                if (row) void openView(row);
              }}
            />
            <GridButton
              hint="Edit user"
              icon="edit"
              onClick={(e) => {
                const row = e.row?.data as ClientUserGridRow | undefined;
                if (row) void openEdit(row);
              }}
            />
            <GridButton
              hint="Delete user"
              icon="trash"
              onClick={(e) => {
                const row = e.row?.data as ClientUserGridRow | undefined;
                if (row) void removeRow(row);
              }}
            />
          </Column>
          <Column
            name="id"
            dataField="id"
            caption="User ID"
            width={280}
            visible={false}
            allowFiltering
            allowHeaderFiltering
          />
          <Column name="displayName" dataField="displayName" caption="Name" minWidth={180} sortOrder="asc" />
          <Column name="userPrincipalName" dataField="userPrincipalName" caption="UPN" minWidth={200} />
          <Column name="mail" dataField="mail" caption="Email" minWidth={200} />
          <Column
            name="accountEnabled"
            dataField="accountEnabled"
            caption="Account status"
            dataType="boolean"
            width={130}
            trueText="Enabled"
            falseText="Disabled"
            cellRender={(c) => <AccountEnabledPill enabled={Boolean(c.value)} />}
          />
          <Column name="department" dataField="department" caption="Department" minWidth={140} />
          <Column name="jobTitle" dataField="jobTitle" caption="Job title" minWidth={140} />
          <Column name="company" dataField="company" caption="Company" minWidth={140} />
          <Column
            name="createdDateTime"
            dataField="createdDateTime"
            caption="Created"
            dataType="datetime"
            minWidth={160}
            format="yyyy-MM-dd HH:mm"
          />
          <Column
            name="givenName"
            dataField="givenName"
            caption="First name"
            minWidth={140}
            visible={false}
          />
          <Column
            name="surname"
            dataField="surname"
            caption="Last name"
            minWidth={140}
            visible={false}
          />
          <Column
            name="employeeType"
            dataField="employeeType"
            caption="Client type"
            minWidth={140}
            visible={false}
          />
          <Column
            name="employeeHireDate"
            dataField="employeeHireDate"
            caption="Client onboard date"
            dataType="date"
            minWidth={150}
            format="yyyy-MM-dd"
            visible={false}
          />
          <Column
            name="officeLocation"
            dataField="officeLocation"
            caption="Office location"
            minWidth={140}
            visible={false}
          />
          <Column
            name="businessPhone"
            dataField="businessPhone"
            caption="Business phone"
            minWidth={140}
            visible={false}
          />
        </DataGrid>
      </div>

      <HvgClientUserFormPopup
        visible={editorOpen}
        mode={formMode}
        editingId={editingId}
        form={form}
        formError={formError}
        saving={saving}
        createProgress={createProgress}
        createdUser={createdUser}
        groupOptions={groupOptions}
        domainOptions={domainOptions}
        domainsError={domainsError}
        loadingGroups={loadingGroups}
        loadingDomains={loadingDomains}
        activeTab={formTab}
        hasP2License={hasP2License}
        p2AvailableUnits={p2AvailableUnits}
        p2LicenseBusy={p2LicenseBusy}
        onActiveTabChange={setFormTab}
        onClose={closeEditor}
        onSave={() => void save()}
        onDelete={() => void removeFromEditor()}
        onSwitchToEdit={switchEditorToEdit}
        onAssignP2={() => void assignP2FromEditor()}
        onRemoveP2={() => void removeP2FromEditor()}
        onFormChange={onFormChange}
      />

      <HvgClientGuestInvitePopup
        visible={guestInviteOpen}
        form={guestForm}
        formError={guestFormError}
        saving={guestSaving}
        progress={guestProgress}
        groupOptions={guestGroupOptions}
        loadingGroups={loadingGuestGroups}
        activeTab={guestFormTab}
        onActiveTabChange={setGuestFormTab}
        onClose={closeGuestInvite}
        onInvite={() => void inviteGuest()}
        onFormChange={(patch) => setGuestForm((prev) => ({ ...prev, ...patch }))}
      />
    </div>
  );
}
